import Axios, { AxiosRequestConfig, AxiosInstance } from "axios";
import { environment } from "../environment";
import { registerAxiosInstance } from "./AxiosTokenManager";
import { addUnauthorizedInterceptor } from "./UnauthorizedInterceptor";

// Create initial core API instance with placeholder baseURL
export const coreAxiosInstance: AxiosInstance = Axios.create({
  baseURL: "http://localhost:8000", // placeholder until environment loads
});

// Register for token management immediately
registerAxiosInstance(coreAxiosInstance);

// Handle 401 responses by redirecting to auth flow
addUnauthorizedInterceptor(coreAxiosInstance);

let isInitialized = false;

// Initialize core API instance baseURL asynchronously
const initializeCoreAxiosInstance = async (): Promise<AxiosInstance> => {
  if (isInitialized) {
    return coreAxiosInstance;
  }

  const env = await environment();

  // Update the baseURL on the existing instance
  coreAxiosInstance.defaults.baseURL = env.BASE_URL;

  isInitialized = true;
  return coreAxiosInstance;
};

// Export for direct use if needed
export const getCoreAxiosInstance = initializeCoreAxiosInstance;

// Core API mutator for orval
export const customCoreInstance = async <T>(
  config: AxiosRequestConfig,
  options?: AxiosRequestConfig
): Promise<T & { cancel?: () => void }> => {
  const coreAxiosInstance = await getCoreAxiosInstance();
  const controller = new AbortController();
  await initializeCoreAxiosInstance(); // Ensure baseURL is set

  const promise = coreAxiosInstance({
    ...config,
    ...options,
    signal: controller.signal,
    // Prefer caller headers (e.g. If-Match) over the generated defaults when
    // both set the same key — Axios shallow-merges `headers` objects, so
    // spread order alone is not enough for nested header maps.
    headers: {
      ...config.headers,
      ...options?.headers,
    },
  }).then((response: { data: T; headers?: Record<string, unknown> }) => {
    const data = response.data;
    // EntityRecordView omits etag in the OpenAPI body; the API returns it as
    // the ETag response header (see generated core-api EntityRecordView doc).
    // Surface it on the payload so PATCH callers can send If-Match.
    const etag = response.headers?.etag ?? response.headers?.ETag;
    if (
      etag != null &&
      data !== null &&
      typeof data === "object" &&
      !Array.isArray(data)
    ) {
      return Object.assign(data, { etag: String(etag) });
    }
    return data;
  });

  // Add cancel functionality — Promise doesn't have a cancel property by default;
  // this pattern is used by Orval to support request cancellation.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (promise as any).cancel = () => controller.abort();
  return promise;
};
